import React from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, fmtMAD } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, FileSpreadsheet } from "lucide-react";

export function exportCSV(filename, rows) {
  if (!rows || !rows.length) { toast.error("No data to export"); return; }
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  toast.success("Exported");
}

function AuditLog() {
  const { data, loading } = useFetch("/audit-logs?limit=200", []);
  if (loading) return <Loading />;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b border-border sticky top-0"><tr>
            {["Time", "User", "Action", "Entity", "Description"].map((h) => <th key={h} className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-border">
            {(data || []).map((l) => (
              <tr key={l.id} className="hover:bg-muted/40" data-testid={`audit-${l.id}`}>
                <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{(l.timestamp || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 font-medium">{l.user_name}</td>
                <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-secondary">{l.action}</span></td>
                <td className="px-4 py-3">{l.entity}</td>
                <td className="px-4 py-3 text-muted-foreground">{l.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function Reports() {
  const { data: fin } = useFetch("/finance/summary", []);

  const exportPlayers = async () => { const r = await client.get("/players?sort_by=name_asc"); exportCSV("players.csv", (r.data || []).map((p) => ({ id: p.player_code, name: p.full_name, dob: p.dob, age: p.age, category: p.category, status: p.status, parent_phone: p.parent_phone }))); };
  const exportPayments = async () => { const r = await client.get("/finance/payments"); exportCSV("payments.csv", (r.data || []).map((p) => ({ receipt: p.receipt_number, player: p.player_name, amount: p.amount, type: p.type, month: p.month, status: p.status, date: p.date }))); };

  return (
    <div>
      <PageHeader title="Reports & Audit" subtitle="Financial reports, exports and activity log" testid="reports-page" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Total Revenue</p><p className="text-2xl font-extrabold mt-2 text-emerald-600">{fmtMAD(fin?.revenue)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Total Expenses</p><p className="text-2xl font-extrabold mt-2 text-amber-600">{fmtMAD(fin?.expenses)}</p></Card>
        <Card className="p-5"><p className="text-xs uppercase font-bold text-muted-foreground">Outstanding</p><p className="text-2xl font-extrabold mt-2 text-rose-600">{fmtMAD(fin?.outstanding)}</p></Card>
      </div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <Button variant="outline" onClick={exportPlayers} data-testid="export-players"><FileSpreadsheet size={16} className="me-1" /> Export Players CSV</Button>
        <Button variant="outline" onClick={exportPayments} data-testid="export-payments"><Download size={16} className="me-1" /> Export Payments CSV</Button>
      </div>
      <Tabs defaultValue="audit">
        <TabsList><TabsTrigger value="audit" data-testid="tab-audit">Activity Log</TabsTrigger></TabsList>
        <TabsContent value="audit" className="mt-4"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}
